import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { SearchTemplate } from '@/types';
import { useSearchTemplateByIdQuery } from './hooks';
import {
  getTemplateLoadErrorMessage,
  isSearchTemplateSharedWithUserDomain,
  TEMPLATE_NOT_SHARED_MESSAGE,
} from './templateLoadErrorMessage';
import { searchTabSelectionAtom } from './atoms';

export function useSavedSearchesTemplateLoad() {
  const domainCode = useAtomValue(domainCodeAtom);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { templateId } = useParams();
  const location = useLocation();
  const [searchesTabSelection, setSearchesTabSelection] = useAtom(searchTabSelectionAtom);

  const [searchTemplateForLoad, setSearchTemplateForLoad] = useState<SearchTemplate | undefined>();
  const [templateLoadError, setTemplateLoadError] = useState<string | null>(null);
  const [dismissedTemplateId, setDismissedTemplateId] = useState<number>(0);

  const templateIdFromRoute = useMemo(() => {
    const n = templateId ? Number(templateId) : Number.NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [templateId]);

  const templateIdFromQuery = useMemo(() => {
    const raw = searchParams.get('templateId');
    const n = raw ? Number(raw) : Number.NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [searchParams]);

  const templateIdFromUrl = templateIdFromRoute || templateIdFromQuery;

  const preloadedTemplateFromNavigation = (location.state as { preloadedTemplate?: SearchTemplate })
    ?.preloadedTemplate;

  useEffect(() => {
    if (preloadedTemplateFromNavigation) {
      if (!isSearchTemplateSharedWithUserDomain(preloadedTemplateFromNavigation, domainCode)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- apply a template passed in via navigation state
        setSearchTemplateForLoad(undefined);
        setTemplateLoadError(TEMPLATE_NOT_SHARED_MESSAGE);
        return;
      }
      setSearchTemplateForLoad(preloadedTemplateFromNavigation);
    }
  }, [domainCode, preloadedTemplateFromNavigation]);

  const skipFetchBecauseRowAlreadyLoaded =
    searchTemplateForLoad !== undefined && searchTemplateForLoad.id === templateIdFromUrl;

  const templateByIdQueryEnabled =
    searchesTabSelection === 'Templates' &&
    templateIdFromUrl > 0 &&
    dismissedTemplateId !== templateIdFromUrl &&
    !skipFetchBecauseRowAlreadyLoaded;

  const {
    data: templateFromQuery,
    isSuccess: isTemplateByIdSuccess,
    isError: isTemplateByIdError,
    error: templateByIdError,
  } = useSearchTemplateByIdQuery(templateIdFromUrl, templateByIdQueryEnabled);

  useEffect(() => {
    const tid = templateIdFromUrl > 0;
    const tab = searchParams.get('tab');
    if (tab === 'Templates' || tid) {
      setSearchesTabSelection('Templates');
    }
  }, [searchParams, setSearchesTabSelection, templateIdFromUrl]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clear load error when the template id changes
    setTemplateLoadError(null);
    setDismissedTemplateId(0);
  }, [templateIdFromUrl]);

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates -- apply or fail the template-by-id query */
  useEffect(() => {
    if (!isTemplateByIdSuccess || !templateFromQuery) {
      return;
    }
    if (!isSearchTemplateSharedWithUserDomain(templateFromQuery, domainCode)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reject a template the domain cannot load
      setSearchTemplateForLoad(undefined);
      setTemplateLoadError(TEMPLATE_NOT_SHARED_MESSAGE);
      return;
    }
    setSearchTemplateForLoad(templateFromQuery);
  }, [domainCode, isTemplateByIdSuccess, templateFromQuery]);

  useEffect(() => {
    if (!isTemplateByIdError || !templateByIdError) {
      return;
    }
    if (dismissedTemplateId === templateIdFromUrl) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- surface template-by-id load failure
    setSearchTemplateForLoad(undefined);
    setTemplateLoadError(getTemplateLoadErrorMessage(templateByIdError));
  }, [dismissedTemplateId, isTemplateByIdError, templateByIdError, templateIdFromUrl]);

  function stripTemplateIdFromUrl() {
    if (templateIdFromRoute > 0) {
      navigate('/searchlibrary', { replace: true });
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('templateId');
        return next;
      },
      { replace: true }
    );
  }

  const handleCloseLoadTemplateModal = () => {
    setSearchTemplateForLoad(undefined);
    setSearchesTabSelection('Templates');
    setDismissedTemplateId(templateIdFromUrl);
    stripTemplateIdFromUrl();
  };

  const handleCloseTemplateLoadError = () => {
    const id = templateIdFromUrl;
    setTemplateLoadError(null);
    setDismissedTemplateId(id);
    stripTemplateIdFromUrl();
    if (id > 0) {
      queryClient.removeQueries({ queryKey: ['searchtemplateById', id] });
    }
  };

  return {
    searchTemplateForLoad,
    templateLoadError,
    handleCloseLoadTemplateModal,
    handleCloseTemplateLoadError,
  };
}
